import React, { useState, useEffect, useRef } from 'react';
import BBALoader from '../../components/BBALoader';
import { FaChartLine, FaPencilRuler, FaHardHat, FaBuilding, FaPlus, FaTimes, FaTrash, FaCalculator, FaLightbulb, FaCog, FaSave, FaArrowUp, FaArrowDown, FaPencilAlt } from 'react-icons/fa';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import BackButton from '../../components/BackButton';
import SaveButton from '../../components/SaveButton';
import EditButton from '../../components/EditButton';
import { createKPIParser, formulaTemplates } from '../../utils/formulaParser';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { fetchGroupEmojis, saveGroupEmoji, defaultGroupEmojis } from '../../services/groupEmojiService';
import { suggestNextVersion } from '../../services/hrCollectionsService';
import KpiHtmlTemplateEditor from '../../components/kpi/KpiHtmlTemplateEditor';
import PortalBackground from '../../components/PortalBackground';

// Same full emoji picker the SOP / Job Description template pages use.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const KPITemplates = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('design');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  // CRM-managed group emojis (shown on the portal KPI section headers) + the
  // shared emoji picker, mirroring the SOP / Job Description template pages.
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerMode, setEmojiPickerMode] = useState('card'); // 'card' | 'group'
  const [groupEmojis, setGroupEmojis] = useState(defaultGroupEmojis('kpi'));

  useEffect(() => {
    fetchGroupEmojis('kpi').then(setGroupEmojis).catch(() => {});
  }, []);

  // Company goals state
  const [companyGoals, setCompanyGoals] = useState([
    { id: 1, label: 'Build Revenue', value: '$3.5M', period: 'per quarter', formula: '' },
    { id: 2, label: 'Design Revenue', value: '$4M', period: 'per quarter', formula: '' },
    { id: 3, label: 'Gross Profit', value: '>35%', period: 'per quarter', formula: '' },
  ]);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [editedCompanyGoals, setEditedCompanyGoals] = useState([]);
  

  
  // Formula-related state
  const [formulaParser, setFormulaParser] = useState(null);
  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const [selectedKpiForFormula, setSelectedKpiForFormula] = useState(null);
  const [formulaInput, setFormulaInput] = useState('');
  const [formulaError, setFormulaError] = useState('');
  
  // New formula builder state
  const [formulaBuilder, setFormulaBuilder] = useState([]);
  const [numberInput, setNumberInput] = useState('');
  const [availableOperations] = useState([
    { value: '+', label: 'Add (+)', symbol: '+' },
    { value: '-', label: 'Subtract (-)', symbol: '-' },
    { value: '*', label: 'Multiply (×)', symbol: '*' },
    { value: '/', label: 'Divide (÷)', symbol: '/' },
    { value: '(', label: 'Open Parenthesis', symbol: '(' },
    { value: ')', label: 'Close Parenthesis', symbol: ')' },
  ]);

  // Template management state
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load data on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Update formula parser when company goals change
  useEffect(() => {
    const parser = createKPIParser(companyGoals);
    setFormulaParser(parser);
  }, [companyGoals]);

  // Fetch templates from Firestore
  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const querySnapshot = await getDocs(collection(db, 'kpiTemplates'));
      const fetchedTemplates = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load the most recent template if available
      if (fetchedTemplates.length > 0) {
        const latestTemplate = fetchedTemplates[0];
        setCurrentTemplateId(latestTemplate.id);
        setCompanyGoals(latestTemplate.companyGoals || companyGoals);
        setDepartmentKPIs(latestTemplate.departmentKPIs || {
          design: [],
          construction: [],
          office: []
        });
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };



  // Save template to Firestore
  const saveTemplateToFirestore = async (templateData) => {
    try {
      const docRef = await addDoc(collection(db, 'kpiTemplates'), {
        ...templateData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: docRef.id, ...templateData };
    } catch (error) {
      console.error('Error saving template:', error);
      throw error;
    }
  };

  // Update template in Firestore
  const updateTemplateInFirestore = async (templateId, templateData) => {
    try {
      const docRef = doc(db, 'kpiTemplates', templateId);
      await updateDoc(docRef, {
        ...templateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  };

  // Delete template from Firestore
  const deleteTemplateFromFirestore = async (templateId) => {
    try {
      const docRef = doc(db, 'kpiTemplates', templateId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  };

  const tabs = [
    { id: 'design', label: 'Design', icon: <FaPencilRuler /> },
    { id: 'construction', label: 'Construction', icon: <FaHardHat /> },
    { id: 'office', label: 'Office', icon: <FaBuilding /> },
    // staticEmoji: not a portal group — fixed emoji, no group-emoji pencil,
    // and excluded from the role modal's Department dropdown.
    { id: 'html-template', label: 'HTML Template', staticEmoji: '🎨' }
  ];
  const departmentTabs = tabs.filter((t) => !t.staticEmoji);

  const [departmentKPIs, setDepartmentKPIs] = useState({
    design: [
      {
                 title: 'Designer',
         value: 'View Details',
         department: 'design',
        kpis: [
          {
            title: 'Design Deliverables',
            value: '$800k',
            period: 'per quarter',
            formula: ''
          },
          {
            title: 'Design Mistakes',
            value: '<$3k',
            period: 'per quarter',
            formula: ''
          },
          {
            title: 'Professional Development',
            value: '3 hours',
            period: 'per quarter',
            formula: ''
          },
          {
            title: 'Billable Time',
            value: '80%',
            period: 'of time billed to projects',
            formula: ''
          }
        ]
      }
    ],
    construction: [],
    office: []
  });

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setEditedJob({ ...job });
    setIsJobModalOpen(true);
  };

  // CRM sidebar search deep-links land here as ?open=<role title> — switch to
  // the role's department tab and open its modal. Gated on currentTemplateId so
  // the seeded placeholder roles can't consume the link before the fetch lands.
  const [searchParams] = useSearchParams();
  const pendingOpenRef = useRef(searchParams.get('open'));
  useEffect(() => {
    const openTitle = pendingOpenRef.current;
    if (!openTitle || !currentTemplateId) return;
    for (const [dept, roles] of Object.entries(departmentKPIs)) {
      const role = (roles || []).find((r) => r?.title === openTitle);
      if (role) {
        pendingOpenRef.current = null;
        setActiveTab(dept);
        handleJobClick(role);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentKPIs, currentTemplateId]);

  // Persist the current template doc (roles live inside one kpiTemplates doc, so
  // reordering saves the whole document rather than a per-card `order` field).
  const persistCurrentTemplate = async (nextDepartmentKPIs) => {
    const templateData = {
      name: `KPI Template ${new Date().toLocaleDateString()}`,
      companyGoals: editedCompanyGoals.length > 0 ? editedCompanyGoals : companyGoals,
      departmentKPIs: nextDepartmentKPIs
    };
    if (currentTemplateId) {
      await updateTemplateInFirestore(currentTemplateId, templateData);
    } else {
      const newTemplate = await saveTemplateToFirestore(templateData);
      setCurrentTemplateId(newTemplate.id);
    }
  };

  // Reorder a role card within its department; carries through to the portal.
  const moveRole = async (index, direction) => {
    const list = departmentKPIs[activeTab] || [];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    const newDepartmentKPIs = { ...departmentKPIs, [activeTab]: next };
    setDepartmentKPIs(newDepartmentKPIs);
    try {
      await persistCurrentTemplate(newDepartmentKPIs);
    } catch (error) {
      console.error('Error reordering roles:', error);
      fetchTemplates();
    }
  };

  // Emoji picker handles both the group (section header) emoji and a role's
  // card emoji, matching the SOP / Job Description template pages.
  const handleEmojiSelect = async (emoji) => {
    if (emojiPickerMode === 'group') {
      const updated = await saveGroupEmoji('kpi', activeTab, emoji, groupEmojis);
      setGroupEmojis(updated);
    } else {
      setEditedJob((prev) => ({ ...(prev || {}), emoji }));
    }
    setShowEmojiPicker(false);
  };

  // Click-to-bump the role version (mirrors the SOP / Job Description templates).
  const bumpVersion = () =>
    setEditedJob((prev) => ({ ...(prev || {}), version: suggestNextVersion(prev?.version || '1.0') }));

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleJobSave = async () => {
    if (isEditing) {
      try {
        const updatedJobs = [...departmentKPIs[editedJob.department]];
        if (selectedJob.title === 'New Role') {
          // Adding new job
          updatedJobs.push(editedJob);
        } else {
          // Updating existing job
          const index = updatedJobs.findIndex(job => job.title === selectedJob.title);
          if (index !== -1) {
            updatedJobs[index] = editedJob;
          }
        }
        
        // Update the department KPIs state
        const newDepartmentKPIs = {
          ...departmentKPIs,
          [editedJob.department]: updatedJobs
        };
        setDepartmentKPIs(newDepartmentKPIs);
        
        // Save the template with the updated data
        const templateData = {
          name: `KPI Template ${new Date().toLocaleDateString()}`,
          companyGoals: editedCompanyGoals.length > 0 ? editedCompanyGoals : companyGoals,
          departmentKPIs: newDepartmentKPIs
        };

        if (currentTemplateId) {
          await updateTemplateInFirestore(currentTemplateId, templateData);
        } else {
          const newTemplate = await saveTemplateToFirestore(templateData);
          setCurrentTemplateId(newTemplate.id);
        }
        
        // Close the modal
        setIsJobModalOpen(false);
        setIsEditing(false);
        setSelectedJob(null);
        setEditedJob(null);
      } catch (error) {
        console.error('Error saving role:', error);
        // You might want to show an error message to the user here
      }
    }
  };

  const handleJobCancel = () => {
    setIsJobModalOpen(false);
    setIsEditing(false);
    setSelectedJob(null);
    setEditedJob(null);
  };

  const handleDeleteRole = async () => {
    if (selectedJob) {
      try {
        // Remove the role from the department KPIs (works for both new and existing roles)
        const updatedJobs = departmentKPIs[selectedJob.department].filter(
          job => job.title !== selectedJob.title
        );
        
        const newDepartmentKPIs = {
          ...departmentKPIs,
          [selectedJob.department]: updatedJobs
        };
        setDepartmentKPIs(newDepartmentKPIs);
        
        // Only save to database if there are existing roles or if we have a template ID
        // For newly added roles that haven't been saved yet, we just update the state
        if (currentTemplateId || departmentKPIs[selectedJob.department].length > 0) {
          const templateData = {
            name: `KPI Template ${new Date().toLocaleDateString()}`,
            companyGoals: editedCompanyGoals.length > 0 ? editedCompanyGoals : companyGoals,
            departmentKPIs: newDepartmentKPIs
          };

          if (currentTemplateId) {
            await updateTemplateInFirestore(currentTemplateId, templateData);
          } else {
            const newTemplate = await saveTemplateToFirestore(templateData);
            setCurrentTemplateId(newTemplate.id);
          }
        }
        
        // Close the modal
        setIsJobModalOpen(false);
        setIsEditing(false);
        setSelectedJob(null);
        setEditedJob(null);
      } catch (error) {
        console.error('Error deleting role:', error);
      }
    }
  };

  const handleJobTitleChange = (e) => {
    setEditedJob({ ...editedJob, title: e.target.value });
  };

  const handleKpiChange = (index, field, value) => {
    const updatedKpis = [...editedJob.kpis];
    updatedKpis[index] = { ...updatedKpis[index], [field]: value };
    setEditedJob({ ...editedJob, kpis: updatedKpis });
  };

  const handleAddKpi = () => {
    setEditedJob({
      ...editedJob,
      kpis: [
        ...editedJob.kpis,
        {
          title: 'New KPI',
          value: '0',
          period: 'per quarter',
          formula: ''
        }
      ]
    });
  };

  const handleDeleteKpi = (index) => {
    setEditedJob({
      ...editedJob,
      kpis: editedJob.kpis.filter((_, i) => i !== index)
    });
  };



  const handleDepartmentChange = (e) => {
    setEditedJob({ ...editedJob, department: e.target.value });
  };



  const handleJobModalClose = () => {
    setIsJobModalOpen(false);
    setSelectedJob(null);
  };

  const handleEditCompanyGoals = () => {
    setEditedCompanyGoals(companyGoals.map(goal => ({ ...goal })));
    setIsEditingCompany(true);
  };

  const handleCancelCompanyGoals = () => {
    setIsEditingCompany(false);
    setEditedCompanyGoals([]);
  };

  const handleSaveCompanyGoals = async () => {
    try {
      setIsLoading(true);
      setCompanyGoals(editedCompanyGoals);
      setIsEditingCompany(false);
      
      // Save the current template
      await saveCurrentTemplate();
    } catch (error) {
      console.error('Error saving company goals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save current template to database
  const saveCurrentTemplate = async () => {
    try {
      const templateData = {
        name: `KPI Template ${new Date().toLocaleDateString()}`,
        companyGoals: editedCompanyGoals.length > 0 ? editedCompanyGoals : companyGoals,
        departmentKPIs: departmentKPIs
      };

      if (currentTemplateId) {
        await updateTemplateInFirestore(currentTemplateId, templateData);
      } else {
        const newTemplate = await saveTemplateToFirestore(templateData);
        setCurrentTemplateId(newTemplate.id);
      }
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };



  const handleCompanyGoalChange = (index, field, value) => {
    const updated = [...editedCompanyGoals];
    updated[index][field] = value;
    setEditedCompanyGoals(updated);
  };

  const handleDeleteCompanyGoal = (index) => {
    const updated = [...editedCompanyGoals];
    updated.splice(index, 1);
    setEditedCompanyGoals(updated);
  };

  const handleAddCompanyGoal = () => {
    setEditedCompanyGoals([
      ...editedCompanyGoals,
      { id: Date.now(), label: '', value: '', period: '', formula: '' }
    ]);
  };

  // Initialize formula parser when company goals change
  useEffect(() => {
    const parser = createKPIParser(companyGoals);
    setFormulaParser(parser);
  }, [companyGoals]);

  // Calculate KPI value using formula if present
  const calculateKpiValue = (kpi) => {
    if (!kpi.formula || !formulaParser) {
      return kpi.value;
    }
    
    const result = formulaParser.evaluate(kpi.formula);
    if (result !== null) {
      return formulaParser.formatResult(result, kpi.formula);
    }
    return kpi.value;
  };

  // Handle formula button click
  const handleFormulaClick = (kpi, index) => {
    setSelectedKpiForFormula({ kpi, index });
    setFormulaInput(kpi.formula || '');
    setFormulaError('');
    setShowFormulaModal(true);
    
    // Parse existing formula into builder
    if (kpi.formula) {
      const parsed = parseFormulaToBuilder(kpi.formula);
      setFormulaBuilder(parsed);
    } else {
      setFormulaBuilder([]);
    }

    // Create role-specific formula parser if editing a role KPI
    if (editedJob && editedJob.title) {
      const roleParser = createRoleFormulaParser(editedJob.title);
      setFormulaParser(roleParser);
    }
  };

  const parseFormulaToBuilder = (formula) => {
    // Simple parser to convert formula string back to builder format
    const parts = formula.split(/\s+/);
    const builder = [];
    
    parts.forEach(part => {
      if (part.match(/^\{.*\}$/)) {
        // Variable
        const cleanPart = part.replace(/[{}]/g, '');
        const [category, key] = cleanPart.split('.');
        builder.push({ type: 'variable', category, key, value: part });
      } else if (['+', '-', '*', '/', '(', ')'].includes(part)) {
        // Operation
        builder.push({ type: 'operation', value: part });
      } else if (!isNaN(part)) {
        // Number
        builder.push({ type: 'number', value: part });
      }
    });
    
    return builder;
  };

  // Handle formula save
  const handleFormulaSave = () => {
    const formulaString = buildFormulaString();
    
    if (!formulaParser) return;
    
    const validation = formulaParser.validateFormula(formulaString);
    if (!validation.isValid) {
      setFormulaError(validation.error);
      return;
    }
    
    // Check if we're editing a company goal or department KPI
    if (isEditingCompany && selectedKpiForFormula.kpi) {
      // Company goal
      const updatedGoals = [...editedCompanyGoals];
      updatedGoals[selectedKpiForFormula.index] = {
        ...updatedGoals[selectedKpiForFormula.index],
        formula: formulaString,
        value: calculateKpiValue({ ...updatedGoals[selectedKpiForFormula.index], formula: formulaString })
      };
      setEditedCompanyGoals(updatedGoals);
    } else if (editedJob && selectedKpiForFormula.kpi) {
      // Department KPI
      const updatedKpis = [...editedJob.kpis];
      updatedKpis[selectedKpiForFormula.index] = {
        ...updatedKpis[selectedKpiForFormula.index],
        formula: formulaString,
        value: calculateKpiValue({ ...updatedKpis[selectedKpiForFormula.index], formula: formulaString })
      };
      setEditedJob({ ...editedJob, kpis: updatedKpis });
    }
    
    setShowFormulaModal(false);
    setSelectedKpiForFormula(null);
    setFormulaInput('');
    setFormulaError('');
    setFormulaBuilder([]);
  };





  // Formula builder functions
  const handleAddToFormula = (item) => {
    setFormulaBuilder([...formulaBuilder, item]);
  };

  const handleAddNumberToFormula = () => {
    if (numberInput && !isNaN(numberInput)) {
      setFormulaBuilder([...formulaBuilder, { type: 'number', value: numberInput }]);
      setNumberInput('');
    }
  };

  const handleClearFormula = () => {
    setFormulaBuilder([]);
  };

  const handleRemoveFromFormula = (index) => {
    const updated = formulaBuilder.filter((_, i) => i !== index);
    setFormulaBuilder(updated);
  };

  const buildFormulaString = () => {
    return formulaBuilder.map(item => {
      if (item.type === 'variable') {
        return `{${item.category}.${item.key}}`;
      } else if (item.type === 'operation') {
        return item.value;
      } else if (item.type === 'number') {
        return item.value;
      }
      return '';
    }).join(' ');
  };

  // Handle formula modal close
  const handleFormulaModalClose = () => {
    setShowFormulaModal(false);
    setSelectedKpiForFormula(null);
    setFormulaInput('');
    setFormulaError('');

    setFormulaBuilder([]);
    setNumberInput('');
    
         // Reset formula parser to default
     const parser = createKPIParser(companyGoals);
     setFormulaParser(parser);
  };

  // Generate role-specific variables based on the current role being edited
  const getRoleSpecificVariables = (roleTitle) => {
    if (!roleTitle) return [];
    
    // Count the number of people in this role across all departments
    let count = 0;
    Object.values(departmentKPIs).forEach(department => {
      department.forEach(job => {
        if (job.title === roleTitle) {
          count++;
        }
      });
    });
    
    return [{
      display: `numberOf${roleTitle.replace(/\s+/g, '')}`,
      value: count.toString(),
      category: 'role'
    }];
  };

  // Create formula parser with role-specific variables for role KPIs
  const createRoleFormulaParser = (roleTitle) => {
    const roleVariables = getRoleSpecificVariables(roleTitle);
    
    // Create a custom variables array that includes role-specific variables
    const allVariables = roleVariables.map(v => ({
      id: `role_${v.display}`,
      name: v.display,
      value: v.value
    }));
    
    return createKPIParser(companyGoals, allVariables);
  };

  return (
    <div className="min-h-screen p-6 relative z-0">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div>
          <div className="flex items-center justify-center mb-8 relative">
            <div className="absolute left-0">
              <BackButton onClick={() => navigate('/crm/templates')} />
            </div>
            <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>KPI Templates</h1>
          </div>



          {activeTab !== 'html-template' && (
          <>
          {/* Company Section */}
          <div className="mb-10 relative">
            <h2 className={`text-2xl font-bold text-center mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Company</h2>
            {!isEditingCompany && (
              <button
                className="absolute right-0 top-0 px-4 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                onClick={handleEditCompanyGoals}
              >
                Edit
              </button>
            )}
            {isEditingCompany && (
              <div className="absolute right-0 top-0 flex gap-2">
                <button
                  className={`px-4 py-1 text-sm rounded hover:bg-gray-300 ${darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-800'}`}
                  onClick={handleCancelCompanyGoals}
                >
                  Cancel
                </button>
                <SaveButton onClick={handleSaveCompanyGoals} />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(isEditingCompany ? editedCompanyGoals : companyGoals).map((goal, idx) => (
                <div key={goal.id} className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-800 p-6 flex flex-col items-center`}>
                  {isEditingCompany ? (
                    <>
                      <input
                        className={`text-lg font-medium mb-2 text-center w-full bg-transparent border-b ${darkMode ? 'border-gray-500 text-gray-300 focus:border-red-400' : 'border-gray-300 text-gray-700 focus:border-red-500'} focus:outline-none`}
                        value={goal.label}
                        onChange={e => handleCompanyGoalChange(idx, 'label', e.target.value)}
                        placeholder="KPI Title"
                      />
                      <input
                        className={`text-4xl font-extrabold mb-1 text-center w-full bg-transparent border-b ${darkMode ? 'border-gray-500 text-white focus:border-red-400' : 'border-gray-300 text-gray-900 focus:border-red-500'} focus:outline-none`}
                        value={goal.value}
                        onChange={e => handleCompanyGoalChange(idx, 'value', e.target.value)}
                        placeholder="Value"
                      />
                      <input
                        className={`text-sm text-center w-full bg-transparent border-b ${darkMode ? 'border-gray-500 text-gray-400 focus:border-red-400' : 'border-gray-300 text-gray-500 focus:border-red-500'} focus:outline-none`}
                        value={goal.period}
                        onChange={e => handleCompanyGoalChange(idx, 'period', e.target.value)}
                        placeholder="Period"
                      />
                    </>
                  ) : (
                    <>
                      <span className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{goal.label}</span>
                      <span className={`text-4xl font-extrabold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {goal.formula ? calculateKpiValue(goal) : goal.value}
                      </span>
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{goal.period}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
            {isEditingCompany && (
              <div className="flex justify-center mt-6">
                <button
                  className="flex items-center text-red-600 hover:text-red-700"
                  onClick={handleAddCompanyGoal}
                >
                  <FaPlus className="mr-2" />
                  Add KPI
                </button>
              </div>
            )}
          </div>

          {/* Department Section */}
          <div className="mb-2 relative">
            <h2 className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Department</h2>
            <button
              className="absolute right-0 top-0 flex items-center justify-center w-8 h-8 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors duration-200"
              onClick={() => {
                const newRole = {
                  title: 'New Role',
                  value: 'View Details',
                  department: activeTab,
                  emoji: '',
                  version: '1.0',
                  kpis: [
                    {
                      title: 'New KPI',
                      value: '0',
                      period: 'per quarter',
                      formula: ''
                    }
                  ]
                };
                handleJobClick(newRole);
                setIsEditing(true);
              }}
              title="Add New Role"
            >
              <FaPlus className="w-4 h-4" />
            </button>
          </div>
          </>
          )}
          {/* Tabs */}
          <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-8`}>
            <nav className="-mb-px flex justify-center space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <div key={tab.id} className={`inline-flex items-center border-b-2 ${active ? 'border-red-500' : 'border-transparent'}`}>
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`whitespace-nowrap py-4 px-1 font-medium text-sm flex items-center ${active ? 'text-red-600' : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <span className="mr-2 text-lg leading-none">{tab.staticEmoji || groupEmojis[tab.id] || '📁'}</span>
                      {tab.label}
                    </button>
                    {active && !tab.staticEmoji && (
                      <button
                        type="button"
                        onClick={() => { setEmojiPickerMode('group'); setShowEmojiPicker(true); }}
                        title="Change the emoji shown on the portal section header for this group"
                        className={`ml-1 p-1 rounded ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                      >
                        <FaPencilAlt className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Department KPIs — or the HTML Template editor */}
          {activeTab === 'html-template' ? (
            <KpiHtmlTemplateEditor darkMode={darkMode} />
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(departmentKPIs[activeTab] || []).map((job, index) => (
              <div
                key={index}
                className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer relative`}
                onClick={() => handleJobClick(job)}
              >
                {/* Reorder controls — swap position within the department and save */}
                <div className="absolute top-2 right-2 flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveRole(index, -1); }}
                    disabled={index === 0}
                    title="Move up"
                    className={`p-1 rounded ${index === 0 ? 'opacity-30 cursor-not-allowed' : darkMode ? 'text-gray-300 hover:bg-gray-500' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    <FaArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveRole(index, 1); }}
                    disabled={index === (departmentKPIs[activeTab] || []).length - 1}
                    title="Move down"
                    className={`p-1 rounded ${index === (departmentKPIs[activeTab] || []).length - 1 ? 'opacity-30 cursor-not-allowed' : darkMode ? 'text-gray-300 hover:bg-gray-500' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    <FaArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex flex-col items-center mb-4">
                  {job.emoji && <span className="text-3xl leading-none mb-2">{job.emoji}</span>}
                  <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>{job.title}</h3>
                  <span className={`mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${darkMode ? 'bg-gray-800 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                    v{job.version || '1.0'}
                  </span>
                </div>
                <button className="w-full bg-red-600 text-white py-1.5 px-3 rounded-md hover:bg-red-700 transition-colors duration-200 text-sm">
                  {job.value}
                </button>
              </div>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Job Details Modal */}
      {isJobModalOpen && selectedJob && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl max-w-4xl w-full p-6`}>
            <div className="flex justify-center items-center mb-6 relative">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {isEditing ? 'Edit Role' : 'Role Details'}
              </h2>
              <button
                onClick={handleJobModalClose}
                className={`absolute right-0 ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Department
                  </label>
                  <select
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300'} rounded-md`}
                    value={editedJob.department}
                    onChange={handleDepartmentChange}
                    disabled={!isEditing}
                  >
                    {departmentTabs.map(tab => (
                      <option key={tab.id} value={tab.id}>{tab.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Title
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300'} rounded-md`}
                    value={editedJob.title}
                    onChange={handleJobTitleChange}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Card Emoji
                  </label>
                  {isEditing ? (
                    <button
                      type="button"
                      onClick={() => { setEmojiPickerMode('card'); setShowEmojiPicker(true); }}
                      title="Choose an emoji"
                      className={`w-full h-[42px] flex items-center justify-center rounded-md border text-2xl transition-colors ${darkMode ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 bg-white hover:bg-gray-100'}`}
                    >
                      {editedJob.emoji ? (
                        <span className="leading-none">{editedJob.emoji}</span>
                      ) : (
                        <FaPlus className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-400'}`} />
                      )}
                    </button>
                  ) : (
                    <div className={`w-full h-[42px] flex items-center justify-center rounded-md border text-2xl ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-gray-50 text-gray-900'}`}>
                      {editedJob.emoji || <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>—</span>}
                    </div>
                  )}
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Version
                  </label>
                  {isEditing ? (
                    <button
                      type="button"
                      onClick={bumpVersion}
                      title="Click to update to the next version"
                      className={`w-full h-[42px] inline-flex items-center justify-center gap-1.5 rounded-md border text-base font-semibold transition-colors ${darkMode ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-900 hover:bg-gray-100'}`}
                    >
                      v{editedJob.version || '1.0'}
                      <FaArrowUp className="text-xs text-gray-400" />
                    </button>
                  ) : (
                    <div className={`w-full h-[42px] flex items-center justify-center rounded-md border text-base font-semibold ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-gray-50 text-gray-900'}`}>
                      v{editedJob.version || '1.0'}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {(isEditing ? editedJob : selectedJob).kpis.map((kpi, index) => (
                  <div
                    key={index}
                    className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 border border-gray-200 dark:border-gray-800 relative`}
                  >
                    {isEditing && (
                      <div className="absolute top-2 right-2 flex gap-2">
                        <button
                          onClick={() => handleFormulaClick(kpi, index)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Set Formula"
                        >
                          <FaCalculator />
                        </button>
                        <button
                          onClick={() => handleDeleteKpi(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={kpi.title}
                          onChange={(e) => handleKpiChange(index, 'title', e.target.value)}
                          className={`text-lg font-semibold mb-4 text-center w-full bg-transparent border-b-2 ${darkMode ? 'border-gray-500 text-white focus:border-red-400' : 'border-gray-300 text-gray-900 focus:border-red-500'} focus:outline-none`}
                        />
                        <div className="flex flex-col items-center">
                          <div className="relative w-full">
                            <input
                              type="text"
                              value={kpi.formula ? calculateKpiValue(kpi) : kpi.value}
                              onChange={(e) => handleKpiChange(index, 'value', e.target.value)}
                              className={`text-4xl font-bold mb-2 text-center w-full bg-transparent border-b-2 ${darkMode ? 'border-gray-500 text-white focus:border-red-400' : 'border-gray-300 text-gray-900 focus:border-red-500'} focus:outline-none ${kpi.formula ? 'text-green-600' : ''}`}
                              readOnly={!!kpi.formula}
                            />
                            {kpi.formula && (
                              <div className={`text-xs text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Formula: {kpi.formula}
                              </div>
                            )}
                          </div>
                          <input
                            type="text"
                            value={kpi.period}
                            onChange={(e) => handleKpiChange(index, 'period', e.target.value)}
                            className={`text-sm text-center w-full bg-transparent border-b-2 ${darkMode ? 'border-gray-500 text-gray-400 focus:border-red-400' : 'border-gray-300 text-gray-500 focus:border-red-500'} focus:outline-none`}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>{kpi.title}</h3>
                        <div className="flex flex-col items-center">
                          <span className={`text-4xl font-bold mb-2 ${kpi.formula ? 'text-green-600' : darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {calculateKpiValue(kpi)}
                          </span>
                          {kpi.formula && (
                            <div className={`text-xs text-center mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Formula: {kpi.formula}
                            </div>
                          )}
                          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{kpi.period}</span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {isEditing && (
                <div className="flex justify-center mb-8">
                  <button
                    onClick={handleAddKpi}
                    className="flex items-center text-red-600 hover:text-red-700"
                  >
                    <FaPlus className="mr-2" />
                    Add New KPI
                  </button>
                </div>
              )}

              <div className="flex justify-between">
                {selectedJob && (
                  <button
                    onClick={handleDeleteRole}
                    className="px-4 py-2 bg-red-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-red-700"
                  >
                    Delete Role
                  </button>
                )}
                <div className="flex justify-end space-x-3 ml-auto">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleJobCancel}
                        className={`px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                      >
                        Cancel
                      </button>
                      <SaveButton onClick={handleJobSave} />
                    </>
                  ) : (
                    <>
                      <EditButton onClick={handleEdit} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Modal */}
      {showFormulaModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl max-w-4xl w-full p-6`}>
            <div className="flex justify-center items-center mb-6 relative">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Build KPI Formula
              </h2>
              <button
                onClick={handleFormulaModalClose}
                className={`absolute right-0 ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formula Builder */}
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Build Your Formula
                  </label>
                  
                  {/* Formula Display */}
                  <div className={`p-4 border rounded-xl mb-4 bg-gray-50 dark:bg-gray-800/60 border-gray-200 dark:border-gray-800`}>
                    <div className="text-sm font-medium mb-2">Current Formula:</div>
                    <div className={`font-mono text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formulaBuilder.length > 0 ? buildFormulaString() : 'No formula built yet'}
                    </div>
                  </div>

                  {/* Formula Builder Controls */}
                  <div className="space-y-3">
                    {/* Variables */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Add Variable
                      </label>
                      <select
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300'}`}
                        onChange={(e) => {
                          if (e.target.value) {
                            const [category, key] = e.target.value.split('.');
                            handleAddToFormula({ type: 'variable', category, key, value: `{${category}.${key}}` });
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">Select a variable...</option>
                        {formulaParser && formulaParser.getAvailableVariables().map((variable, idx) => (
                          <option key={idx} value={`${variable.key}`}>
                            {variable.display} ({variable.value})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Operations */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Add Operation
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {availableOperations.map((op) => (
                          <button
                            key={op.value}
                            onClick={() => handleAddToFormula({ type: 'operation', value: op.value })}
                            className={`px-3 py-2 border rounded-md text-sm font-medium ${darkMode ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                          >
                            {op.symbol}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Number Input */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Add Number
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={numberInput}
                          onChange={(e) => setNumberInput(e.target.value)}
                          placeholder="Enter a number"
                          className={`flex-1 px-3 py-2 border rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddNumberToFormula();
                            }
                          }}
                        />
                        <button
                          onClick={handleAddNumberToFormula}
                          className={`px-4 py-2 border rounded-md text-sm font-medium ${darkMode ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Clear Formula */}
                    <button
                      onClick={handleClearFormula}
                      className={`w-full px-3 py-2 border rounded-md text-sm font-medium ${darkMode ? 'border-red-600 bg-red-600 text-white hover:bg-red-700' : 'border-red-500 bg-red-500 text-white hover:bg-red-600'}`}
                    >
                      Clear Formula
                    </button>
                  </div>
                </div>
              </div>

              {/* Formula Preview */}
              <div className="space-y-4">

                {/* Available Variables */}
                {formulaParser && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Available Variables
                    </label>
                    <div className={`p-4 rounded-xl border bg-gray-50 dark:bg-gray-800/60 border-gray-200 dark:border-gray-800`}>
                      <div className="grid grid-cols-1 gap-2 text-xs">
                        {formulaParser.getAvailableVariables().map((variable, idx) => (
                          <div key={idx} className={`p-2 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                            <span className={`font-mono ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{variable.display}</span>
                            <span className={`ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>→ {variable.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {formulaError && (
                  <div className="text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-lg">
                    {formulaError}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleFormulaModalClose}
                className={`px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleFormulaSave}
                className="px-4 py-2 bg-red-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-red-700"
              >
                Save Formula
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full emoji picker — group (section header) or role card emoji */}
      {showEmojiPicker && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1100]"
          onClick={() => setShowEmojiPicker(false)}
        >
          <div
            className={`bg-white dark:bg-gray-900 rounded-2xl p-4 w-full max-w-sm`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={`text-lg font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Choose an icon</h3>
            <div className="flex justify-center">
              <React.Suspense
                fallback={
                  <div className="flex items-center justify-center h-72 w-full">
                    <BBALoader size={32} />
                  </div>
                }
              >
                <EmojiPicker
                  onEmojiClick={(emojiData) => handleEmojiSelect(emojiData.emoji)}
                  theme={darkMode ? 'dark' : 'light'}
                  width="100%"
                  height={380}
                  lazyLoadEmojis
                  previewConfig={{ showPreview: false }}
                />
              </React.Suspense>
            </div>
            <div className="flex items-center justify-between mt-3">
              <button
                type="button"
                onClick={() => handleEmojiSelect('')}
                className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
              >
                {emojiPickerMode === 'group' ? 'Use default' : 'Clear emoji'}
              </button>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(false)}
                className={`px-3 py-2 text-sm font-medium border rounded-md ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPITemplates;